<template>
    <div class="flex flex-col h-full w-full p-2">
        <div v-if="ManualInvoiceDebtCollectionSync.isEnabled">
            <dev-debt-collection-synchronizer />
        </div>
        <div class="flex-container flex items-center justify-between">
            <div class="flex items-center">
                <targeting-router-link v-if="!previewMode" :to="INVOICE_CREATOR" class="targeting-link btn btn-sm btn-primary mr-2">Ny faktura</targeting-router-link>
                <date-range-selector v-model="dateRange" class="my-2"/>
            </div>
            <UInput
                v-model="searchText"
                type="search"
                placeholder="Faktura, kunde, dato"
                class="general-input w-1/4"
                icon="i-ph-magnifying-glass"
                @update:model-value="onFilterTextBoxChanged"
            />
        </div>
        <div class="flex flex-col items-end">
            <div class="text-sm text-base-content/50">Sum ikke betalt</div>
            <div class="text-lg"><span class="text-base-content/50">kr</span> {{ balance }}</div>
        </div>
        <div class="flex mb-2 ">
            <button
                class="btn btn-sm rounded-r-none text-lg"
                title="Listevisning"
                :class="[previewMode === false ? 'btn-active' : '']"
                @click="() => {
                    previewMode = false
                    gridApi?.refreshCells({ force: true })
                }"
            >
                <i class="i-ph-list-bullets" />
            </button>
            <button
                class="btn btn-sm rounded-l-none text-lg"
                title="Forhåndsvisning"
                :class="[previewMode === true ? 'btn-active' : '']"
                @click="() => {
                    previewMode = true
                    gridApi?.refreshCells({ force: true })
                }"
            >
                <i class="i-ph-layout" />
            </button>
        </div>
        <div class="flex w-full h-full gap-1">
            <ag-grid-vue
                class="w-full"
                :theme="agTheme"
                :grid-options="gridOptions"
                :row-data="invoicesWithBalanceWithDueDate"
                :column-defs="columnDefs"
            />
            <invoice-view
                v-if="previewMode && selectedInvoice"
                :invoice-uuid="selectedInvoice?.uuid"
                class="w-full"
            />
        </div>

        <invoice-debt-collection-popup
            v-if="selectedInvoice && isModalOpen"
            :invoice-balance="selectedInvoice"
            @close="closeModal"
        />
    </div>
</template>

<script setup lang="ts">

import {AgGridVue} from 'ag-grid-vue3'
import {computed, inject, onMounted, onUnmounted, Ref, ref, shallowRef, watch} from 'vue'
import {ColDef, GetContextMenuItemsParams, GridApi, GridOptions, GridReadyEvent, ICellRendererParams, ValueGetterParams} from 'ag-grid-community'
import InvoiceSaccCellRenderer from '@/invoice/InvoiceSaccCellRenderer.vue'
import {InvoiceBalance} from '@/invoice/InvoiceBalance'
import {getInvoiceBalancesWithDueDate} from '@/invoice/invoice.service'
import {INVOICE_CREATOR} from '@/invoice'
import {formatCurrency, formattedDate} from '@/services/formatter.service'
import {endOfYear, format, startOfYear} from 'date-fns'
import {DELETE_TRANSACTION, TRANSACTION_CREATED, TRANSACTION_UPDATED} from '@/types/transaction/TransactionEvent'
import {ORG_SWITCHED} from '@/types/events'
import {useEmitter} from '@/services/infrastructure/emitter'
import DateRangeSelector from '@/dateUtils/DateRangeSelector.vue'
import {DateRange} from '@/dateUtils/DateRange'
import {agTheme} from '@/agUtils/ag-theme.service'
import DueDaysCellRenderer from '@/invoice/DueDaysCellRenderer.vue'
import { TargetingRouterLink } from '@tritt/multipanel'
import {getPersistedRangeOr} from '@/dateUtils/dateRange.service'
import InvoiceLinkCell from '@/invoice/InvoiceLinkCell.vue'
import {findSubAccountByUuid} from '@/subaccount/subAccount.service'
import InvoiceDebtCollectionPopup from '@/invoice/invoiceDebtCollectionPopup.vue'
import InvoiceDebtCollectionCell from '@/invoice/InvoiceDebtCollectionCell.vue'
import {ShowDebtCollection} from '@/tenants/org.features'
import {globalDefaultColDef} from '@/agUtils'
import {showExportsDialog} from '@/exports/exports.service'
import {ReportType} from '@/types/reports/Report'
import InvoiceView from '@/invoice/InvoiceView.vue'
import {Invoice} from '@/invoice/types/Invoice'
import {ManualInvoiceDebtCollectionSync} from '@/invoice/feature-toggles'
import DevDebtCollectionSynchronizer from '@/invoice/DevDebtCollectionSynchronizer.vue'
import {INVOICE_CREATED} from '@/types/invoice/InvoiceEvent'

const previewMode = ref(false)

const {onEvent, cleanUp} = useEmitter()
const gridApi = shallowRef<GridApi>()

const searchText = ref('')
const panelId: Ref<number> | undefined = inject('panelId')
const panelPathSegment: Ref<string> | undefined = inject('panelPathSegment')

const dateRange = ref<DateRange>(getPersistedRangeOr({
    start: startOfYear(new Date()),
    end: endOfYear(new Date())
}))

const isModalOpen = ref(false)
const selectedInvoice = ref<InvoiceBalance>()

const handleInvoiceSelected = (invoice: InvoiceBalance) => {
    selectedInvoice.value = invoice
}

const openModal = (invoice: InvoiceBalance) => {
    selectedInvoice.value = invoice
    isModalOpen.value = true
}

const closeModal = () => {
    isModalOpen.value = false
    selectedInvoice.value = undefined
}

const invoicesWithBalanceWithDueDate = ref<InvoiceBalance[]>([])

const balance = computed(() => {
    return formatCurrency(invoicesWithBalanceWithDueDate.value.reduce((acc, invoice) => {
        return acc + invoice.balance
    }, 0), 2)
})

const onFilterTextBoxChanged = () => {
    gridApi.value?.setGridOption('quickFilterText', searchText.value)
}

const gridOptions: GridOptions = {
    suppressContextMenu: true,
    getRowId: (params: { data: Invoice }) => params.data.uuid || 'na',
    context: {
        panelId,
        panelPathSegment,
        refresh: () => {
            return fetchInvoices()
        }
    },
    onGridReady: ({api}: GridReadyEvent) => {
        gridApi.value = api
        gridApi.value.setGridOption('loading', true)
        fetchInvoices()
        api.applyColumnState({
            state: [
                {colId: 'dueDays', sort: 'asc'}
            ]
        })
    },
    defaultColDef: {
        ...globalDefaultColDef,
        sortable: true,
        resizable: true
    },
    enableCellTextSelection: true,
    suppressCellFocus: true,
    overlayLoadingTemplate: '<span class="text-gray-500">Laster...</span>',
    overlayNoRowsTemplate: '<span class="text-gray-500">Ingen fakturaer</span>',
    getContextMenuItems: (params: GetContextMenuItemsParams) => {
        const items = (params.defaultItems || []).filter( item => item !== 'export')
        return [
            ...items,
            {
                name: 'Eksporter...',
                icon: '<span class ="ag-icon ag-icon-save" />',
                action: showInvJouReportExportsDialog
            }
        ]
    }
}

const columnDefs: ColDef[] = [
    {
        headerName: 'Fakturanummer',
        field: 'invoiceNo',
        flex: 1,
        minWidth: 130,
        maxWidth: 200,
        cellRenderer: InvoiceLinkCell,
        onCellClicked: (event) => handleInvoiceSelected(event.data),
        cellRendererParams: () => ({
            previewMode: previewMode.value
        })
    },
    {
        headerName: 'Dato',
        field: 'date',
        width: 110,
        cellRenderer: (params: ValueGetterParams) => {
            return formattedDate(params.data.date)
        },
    },
    {
        headerName: 'Forfallsdato',
        field: 'dueDate',
        width: 117,
        cellRenderer: (params: ValueGetterParams) => {
            return formattedDate(params.data.dueDate)
        }
    },
    {
        headerName: 'Kunde',
        cellRenderer: InvoiceSaccCellRenderer,
        flex: 1,
        minWidth: 90,
        valueGetter: (params: ValueGetterParams) => {
            const subAccount = findSubAccountByUuid(params.data.subAccountUuid)
            return subAccount ? subAccount.name : ''
        }
    },
    {
        headerName: 'Type',
        field: 'invoiceType',
        width: 80,
        hide: true
    },
    {
        headerName: 'Beløp',
        valueGetter: (params: ValueGetterParams) => {
            return params.data.amount
        },
        minWidth: 90,
        width: 120,
        headerClass: 'header-right',
        cellStyle: {'text-align': 'right'},
        cellRenderer: (node: ICellRendererParams) => formatCurrency(node.value, 2)
    },
    {
        headerName: 'Restbeløp',
        field: 'balance',
        width: 120,
        headerClass: 'header-right',
        cellStyle: {'text-align': 'right'},
        cellRenderer: (node: ICellRendererParams) => formatCurrency(node.value, 2),
        sortable: true
    },
    {
        headerName: 'Forfall',
        field: 'dueDays',
        headerClass: 'header-right',

        cellRenderer: DueDaysCellRenderer,
        cellRendererParams: ({data}: ICellRendererParams) => {
            return {
                display: data.dueDays,
                balance: data.balance
            }
        },
        resizable: false,
        width: 160
    },
    {
        headerName: 'Betalingsoppfølging',
        field: 'status',
        cellStyle: {'text-align': 'right'},
        valueGetter: (params: ValueGetterParams) => {
            return params.data.debtCollectionStatus
        },
        cellRenderer: InvoiceDebtCollectionCell,
        cellRendererParams: {
            openModal: openModal
        },
        headerClass: 'header-right',
        maxWidth: 300,
        minWidth: 100,
        flex: 1,
        hide: !ShowDebtCollection.isEnabled(),
    }
]

const clear = () => {
    invoicesWithBalanceWithDueDate.value = []
}

const fetchInvoices = async () => {
    gridApi.value?.setGridOption('loading', true)
    invoicesWithBalanceWithDueDate.value = await getInvoiceBalancesWithDueDate(dateRange.value.start, dateRange.value.end)
    if (invoicesWithBalanceWithDueDate.value?.length === 0) {
        gridApi.value?.showNoRowsOverlay()
    } else {
        selectedInvoice.value = invoicesWithBalanceWithDueDate.value[0]
    }
    gridApi.value?.setGridOption('loading', false)
}

const showInvJouReportExportsDialog = () => {
    if(!dateRange.value.start || !dateRange.value.end) {
        console.error('From date and to date must be set before exporting the general ledger report.')
        return
    }
    showExportsDialog(
        ReportType.INVJOU,
        {
            fromDate: format(dateRange.value.start, 'yyyy-MM-dd'),
            toDate: format(dateRange.value.end, 'yyyy-MM-dd'),
        }
    )
}

onMounted(() => {
    onEvent(INVOICE_CREATED, fetchInvoices)
    onEvent(TRANSACTION_CREATED, fetchInvoices)
    onEvent(TRANSACTION_UPDATED, fetchInvoices)
    onEvent(DELETE_TRANSACTION, fetchInvoices)
    onEvent(ORG_SWITCHED, () => {
        clear()
        fetchInvoices()
    })
})

onUnmounted(cleanUp)

watch(dateRange, async () => {
    await fetchInvoices()
})

</script>

<style scoped>
@reference "@/index.css";
@reference "tailwindcss";

.flex-container {
    display: flex;
    flex-wrap: wrap;
}


@media (max-width: 800px) {
    .general-input {
        @apply w-1/3;
        order: 1;
    }
    @media (max-width: 550px) {
        .targeting-link {
            order: 2;
        }
    }
}


</style>